import React, { useState, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import useBackgroundDetection from "../../hooks/useBackgroundDetection";

// Set to true to re-enable the banner
const BANNER_ENABLED = false;

const FlyCodeBanner = () => {
  // Hooks must be called unconditionally (React Rules of Hooks)
  const tickerRef = useRef(null);
  const animationRef = useRef(null);
  const positionRef = useRef(0);
  const isPausedRef = useRef(false);

  // Simple scroll-based detection (only active when banner is enabled)
  const { isDarkMode: finalIsDarkMode } = useBackgroundDetection({
    scrollThreshold: 0.7,
    enableScrollDetection: BANNER_ENABLED,
    defaultMode: "dark",
  });

  // FlyCode-style ticker items
  const tickerItems = [
    {
      text: "Explore how EduTools helps greek chapters thrive",
      link: "/greek",
      readMoreText: "Read more →",
    },
    {
      text: "Trusted by students around the nation",
      link: "/greek",
      readMoreText: "Read more →",
    },
    {
      text: "How much can your organization save?",
      link: "/greek",
      readMoreText: "See now →",
    },
    {
      text: "Instagram",
      link: "https://instagram.com/edutoo1s",
      isExternal: true,
      icon: "instagram",
      readMoreText: "Check it out →",
    },
  ];

  // Background detection is now handled by the useBackgroundDetection hook

  // Ultra-smooth ticker animation with refs to avoid state updates
  useEffect(() => {
    if (!BANNER_ENABLED) return;

    const ticker = tickerRef.current;
    if (!ticker) return;

    const animate = () => {
      if (!isPausedRef.current) {
        positionRef.current -= 0.15; // Slower speed to match FlyCode's elegant pace

        // Calculate the width of one complete set of items (not including duplicates)
        const singleSetWidth = ticker.scrollWidth / 3;

        // When we've scrolled past one complete set, reset to continue seamlessly
        if (positionRef.current <= -singleSetWidth) {
          positionRef.current += singleSetWidth;
        }
      }

      ticker.style.transform = `translateX(${positionRef.current}px)`;
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Banner is disabled - return null to hide it (after hooks are called)
  if (!BANNER_ENABLED) {
    return null;
  }

  const handleMouseEnter = () => {
    isPausedRef.current = true;
  };

  const handleMouseLeave = () => {
    isPausedRef.current = false;
  };

  // Function to scroll to top when navigating
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  };

  return (
    <Box
      sx={{
        background: finalIsDarkMode
          ? "#1a1a1a"
          : "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
        padding: "12px 0 12px 0", // Even top and bottom padding (~40px total)
        textAlign: "center",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        overflow: "hidden",
        zIndex: 1400,
        transition: "all 0.6s ease",
        borderBottom: finalIsDarkMode
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      }}
    >
      {/* Subtle pattern overlay - adapts to light/dark mode */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: finalIsDarkMode
            ? 'url(\'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="banner-pattern-dark" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23banner-pattern-dark)"/></svg>\')'
            : 'url(\'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="banner-pattern-light" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="rgba(0,0,0,0.05)"/></pattern></defs><rect width="100" height="100" fill="url(%23banner-pattern-light)"/></svg>\')',
          opacity: 0.3,
          pointerEvents: "none",
          transition: "background 0.6s ease",
        }}
      />

      {/* FlyCode-style centered ticker */}
      <Box
        sx={{
          position: "relative",
          height: "24px", // Increased height for larger text
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {/* Gradient fade effects */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "120px",
            background: `linear-gradient(to right, ${
              finalIsDarkMode ? "#1a1a1a" : "#f8f9fa"
            }, transparent)`,
            zIndex: 2,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: "120px",
            background: `linear-gradient(to left, ${
              finalIsDarkMode ? "#1a1a1a" : "#f8f9fa"
            }, transparent)`,
            zIndex: 2,
            pointerEvents: "none",
          }}
        />

        {/* Ticker content */}
        <Box
          ref={tickerRef}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "120px", // Increased spacing for better separation
            whiteSpace: "nowrap",
            willChange: "transform",
          }}
        >
          {/* Triple items for seamless loop */}
          {[...tickerItems, ...tickerItems, ...tickerItems].map(
            (item, index) => (
              <Box
                key={`${item.text}-${index}`}
                component={item.isExternal ? "a" : Link}
                href={item.isExternal ? item.link : undefined}
                to={item.isExternal ? undefined : item.link}
                target={item.isExternal ? "_blank" : undefined}
                rel={item.isExternal ? "noopener noreferrer" : undefined}
                onClick={item.isExternal ? undefined : scrollToTop}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "16px 24px",
                  cursor: "pointer",
                  textDecoration: "none",
                  borderRadius: "24px",
                  margin: "0 4px",
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                {item.icon === "instagram" && (
                  <img
                    src="./images/instagram-logo-black-transparent.png"
                    alt="Instagram"
                    style={{
                      width: "20px",
                      height: "20px",
                      objectFit: "contain",
                      filter: finalIsDarkMode ? "invert(1)" : "none",
                      flexShrink: 0,
                    }}
                  />
                )}
                <Typography
                  sx={{
                    fontSize: "14px", // Increased text size
                    color: finalIsDarkMode
                      ? "rgba(255, 255, 255, 0.8)"
                      : "rgba(0, 0, 0, 0.7)",
                    fontWeight: 500,
                  }}
                >
                  {item.text}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px", // Increased "Read more" text size
                    color: finalIsDarkMode
                      ? "rgba(255, 255, 255, 0.6)"
                      : "rgba(0, 0, 0, 0.5)",
                    fontWeight: 400,
                    textDecoration: "underline",
                  }}
                >
                  {item.readMoreText}
                </Typography>
              </Box>
            )
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FlyCodeBanner;
